import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

// On 401: try one silent refresh, retry the request; else bounce to /login
let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (response?.status === 401 && !config._retried && !config.url.includes("/auth/")) {
      config._retried = true;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`, null, { withCredentials: true });
        await refreshing;
        refreshing = null;
        return http(config);
      } catch (e) {
        refreshing = null;
        if (window.location.pathname !== "/login") window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  stats: () => http.get("/stats").then(r => r.data),
  signals: (params = {}) => http.get("/signals", { params }).then(r => r.data),
  signal: (id) => http.get(`/signals/${id}`).then(r => r.data),
  insights: (params = {}) => http.get("/insights", { params }).then(r => r.data),
  insight: (id) => http.get(`/insights/${id}`).then(r => r.data),
  promoteInsight: (id, body) => http.post(`/insights/${id}/promote`, body, { timeout: 180000 }).then(r => r.data),
  insightChannels: () => http.get("/insights/channels").then(r => r.data),
  opportunities: (params = {}) => http.get("/opportunities", { params }).then(r => r.data),
  opportunity: (id) => http.get(`/opportunities/${id}`).then(r => r.data),
  opportunityMemoStatus: () => http.get("/opportunities/memos/status").then(r => r.data),
  backfillOpportunityMemos: () => http.post("/opportunities/memos/backfill", {}, { timeout: 60000 }).then(r => r.data),
  retryOpportunityMemo: (id) => http.post(`/opportunities/${id}/memo/retry`).then(r => r.data),
  launches: () => http.get("/launches").then(r => r.data),
  simulate: (body) => http.post("/simulate", body).then(r => r.data),
  search: (query) => http.post("/search", { query }).then(r => r.data),
  copilotHistory: (sid) => http.get(`/copilot/history/${sid}`).then(r => r.data),
  // Ingestion
  adapters: () => http.get("/ingest/adapters").then(r => r.data),
  ingestRuns: (limit = 100) => http.get("/ingest/runs", { params: { limit } }).then(r => r.data),
  runIngest: (slug) => http.post("/ingest/run", null, { params: slug ? { slug } : {}, timeout: 300000 }).then(r => r.data),
  // Retraining
  retrainStat: (launchId) => http.post(`/retrain/${launchId}`).then(r => r.data),
  retrainHistory: (launchId) => http.get(`/retrain/${launchId}/history`).then(r => r.data),
  retrainHistoryAll: (limit = 50) => http.get("/retrain/history", { params: { limit } }).then(r => r.data),
  // PDF + Email
  emailStatus: () => http.get("/email/status").then(r => r.data),
  emailMemo: (oppId, recipient) => http.post(`/opportunities/${oppId}/email`, null, { params: recipient ? { recipient } : {} }).then(r => r.data),
  pdfUrl: (oppId) => `${API}/opportunities/${oppId}/pdf`,
  // Projects
  projects: () => http.get("/projects").then(r => r.data),
  project: (idOrSlug) => http.get(`/projects/${idOrSlug}`).then(r => r.data),
  // Concepts
  listConcepts: (project_id) => http.get("/concepts", { params: project_id ? { project_id } : {} }).then(r => r.data),
  promoteConcept: (conceptId) => http.post(`/concepts/${conceptId}/promote`).then(r => r.data),
  memoizeConcept: (conceptId) => http.post(`/concepts/${conceptId}/memoize`, null, { timeout: 180000 }).then(r => r.data),
  generatePackshot: (conceptId) => http.post(`/concepts/${conceptId}/packshot`, null, { timeout: 180000 }).then(r => r.data),
  // Cultural signals
  culturalSignals: (params = {}) => http.get("/cultural-signals", { params }).then(r => r.data),
  refreshCulturalSignals: (hashtag) => http.post("/cultural-signals/refresh", null, { params: hashtag ? { hashtag } : {} }).then(r => r.data),
  recentHashtagRuns: (limit = 5) => http.get("/cultural-signals/runs/recent", { params: { limit } }).then(r => r.data),
  // Offline signals (replaces wellness formats)
  offlineSignals: (params = {}) => http.get("/offline-signals", { params }).then(r => r.data),
  // Briefs
  briefs: (params = {}) => http.get("/briefs", { params }).then(r => r.data),
  brief: (id) => http.get(`/briefs/${id}`).then(r => r.data),
  issueRfpFromBrief: (briefId) => http.post(`/briefs/${briefId}/issue-rfp`).then(r => r.data),
  // Offline signals (create)
  createOfflineSignal: (payload) => http.post("/offline-signals", payload).then(r => r.data),
  uploadOfflineSignalPhoto: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/uploads/offline-signal-photo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  // Suppliers
  suppliers: (params = {}) => http.get("/suppliers", { params }).then(r => r.data),
  // RFPs
  rfps: (params = {}) => http.get("/rfps", { params }).then(r => r.data),
  // Opportunity inline-edit
  patchOpportunity: (id, patch) => http.patch(`/opportunities/${id}`, patch).then(r => r.data),
  // NeedScope
  needscope: () => http.get("/projects/needscope").then(r => r.data),
  needscopeProject: (idOrSlug) => http.get(`/projects/${idOrSlug}/needscope`).then(r => r.data),
  // Prediction loop
  loopStats: () => http.get("/loop-stats").then(r => r.data),
  // Team / access control (admin)
  listUsers: () => http.get("/users").then(r => r.data),
  createUser: (body) => http.post("/users", body).then(r => r.data),
  updateUser: (id, body) => http.patch(`/users/${id}`, body).then(r => r.data),
  deleteUser: (id) => http.delete(`/users/${id}`).then(r => r.data),
};

// Streaming copilot via fetch + ReadableStream
export async function* streamCopilot(sessionId, message) {
  const res = await fetch(`${API}/copilot/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  yield* parseSSE(res);
}

// Streaming retraining oracle
export async function* streamRetrain(launchId) {
  const res = await fetch(`${API}/retrain/${launchId}/ai`, { method: "POST", credentials: "include" });
  yield* parseSSE(res);
}

// Streaming concept generator
export async function* streamConcept(body) {
  const res = await fetch(`${API}/concepts/generate`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  yield* parseSSE(res);
}

async function* parseSSE(res) {
  if (!res.ok) {
    throw new Error(`Stream request failed (HTTP ${res.status})`);
  }
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      const m = line.match(/^data:\s*(.*)$/m);
      if (m) {
        try { yield JSON.parse(m[1]); } catch (e) {
          // Non-JSON SSE frame (heartbeat / partial chunk) — ignore by design
          console.debug("[SSE] skipped non-JSON frame:", m[1]?.slice(0, 80));
        }
      }
    }
  }
}
